import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        await axios.post(`${API}/auth/verify-email?token=${token}`);
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        setStatus('error');
        toast.error(error.response?.data?.detail || 'Verification failed');
      }
    };

    verifyEmail();
  }, [token]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FF2E63] animate-spin mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white">Verifying your email...</h1>
          <p className="text-[#A1A1AA] mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#08D9D6]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Email Verified!</h1>
          <p className="text-[#A1A1AA] mb-6">
            Your email has been verified successfully. Your account is now more secure.
          </p>
          <Button
            onClick={() => navigate('/')}
            data-testid="go-home-btn"
            className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
          >
            Continue to PULSE
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
        <p className="text-[#A1A1AA] mb-6">
          {!token 
            ? 'No verification token provided. Please check your email link.'
            : 'The verification link may have expired or already been used.'
          }
        </p>
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/my-profile')}
            data-testid="resend-verification-btn"
            className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
          >
            Go to Profile to Resend
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="w-full text-[#A1A1AA] hover:text-white"
          >
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
